import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const STORAGE_KEY = 'dripfield:spin-to-win:claimed';
const PENDING_KEY = 'dripfield:spin-to-win:pending-confirmation';
const DISCOUNT_CODE = 'DRIP25';
const SEGMENT_COUNT = 6;

/**
 * Renders the DripField "Spin to Win" wheel.
 *
 * Every segment awards the same real prize (the existing DRIP25 discount code) —
 * the randomness only affects where the wheel visually lands, never the outcome.
 * This component never creates, requests, or modifies any Shopify discount; it only
 * displays and copies the fixed DRIP25 code string.
 *
 * @extends {Component<{wheelScreen: HTMLElement, resultScreen: HTMLElement, wheel: HTMLElement, spinButton: HTMLButtonElement, copyButton: HTMLButtonElement, confetti: HTMLElement}>}
 */
export class SpinToWin extends Component {
  requiredRefs = ['wheelScreen', 'resultScreen', 'wheel', 'spinButton', 'copyButton', 'confetti'];

  #currentRotation = 0;

  connectedCallback() {
    super.connectedCallback();

    if (this.#hasAlreadyClaimed()) {
      // The floating launcher lives outside this component (a sibling in the
      // widget wrapper, so the game can already be won by the time this loads)
      // — once claimed, it must never be shown again, on this load or any later one.
      this.#hideLauncher();

      // If we just redirected through /discount/DRIP25 to apply the code, this
      // page load is the customer landing back where they started — surface the
      // confirmation immediately. Otherwise (e.g. they click the gift button on
      // some later, unrelated visit) just show the applied state quietly without
      // popping the dialog open on its own.
      const justApplied = this.#consumePendingConfirmation();
      this.#showResultScreen({ animate: justApplied });

      if (justApplied) this.#openDialog();
    }
  }

  /**
   * Spins the wheel. Bound as a class field so it can be used as an `on:click` handler.
   */
  spin = () => {
    const { wheel, spinButton } = this.refs;

    if (spinButton.disabled) return;
    spinButton.disabled = true;
    spinButton.setAttribute('aria-busy', 'true');

    const segmentAngle = 360 / SEGMENT_COUNT;
    // Land safely inside a segment (never exactly on a dividing line).
    const margin = segmentAngle * 0.18;
    const landingOffset = margin + Math.random() * (segmentAngle - margin * 2);
    const fullSpins = 6 + Math.floor(Math.random() * 3); // 6-8 full rotations for a satisfying spin
    const targetRotation = this.#currentRotation + fullSpins * 360 + landingOffset;

    this.#currentRotation = targetRotation;

    const reducedMotion = prefersReducedMotion();
    const duration = reducedMotion ? 350 : 4600;

    wheel.style.transition = `transform ${duration}ms cubic-bezier(0.11, 0.79, 0.15, 1)`;
    wheel.style.transform = `rotate(${targetRotation}deg)`;

    const handleTransitionEnd = (event) => {
      if (event.target !== wheel || event.propertyName !== 'transform') return;
      wheel.removeEventListener('transitionend', handleTransitionEnd);
      this.#applyDiscountAndRedirect();
    };

    wheel.addEventListener('transitionend', handleTransitionEnd);
  };

  /**
   * Applies the real DRIP25 discount using Shopify's native discount-link
   * endpoint (/discount/{code}?redirect={path}), which activates the code on
   * the customer's checkout session server-side. This requires a full-page
   * navigation — there is no AJAX-only equivalent — so we mark the win as
   * claimed and flag that a confirmation is pending before leaving the page,
   * then land the customer back on the exact page (path + query string) they
   * were already on.
   */
  #applyDiscountAndRedirect() {
    this.#setClaimed();
    this.#setPendingConfirmation();

    const returnPath = window.location.pathname + window.location.search;
    window.location.href = `/discount/${DISCOUNT_CODE}?redirect=${encodeURIComponent(returnPath)}`;
  }

  /**
   * Copies the DRIP25 code to the clipboard. Bound as a class field for `on:click`.
   */
  copyCode = async () => {
    const { copyButton } = this.refs;
    const originalLabel = copyButton.dataset.defaultLabel || copyButton.textContent || '';

    let copied = false;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        // navigator.clipboard.writeText() can hang indefinitely in some browser
        // contexts (permission prompts that never resolve, restrictive policies,
        // etc.) instead of rejecting, so race it against a short timeout and
        // fall back to the legacy copy method if it doesn't settle in time.
        await this.#withTimeout(navigator.clipboard.writeText(DISCOUNT_CODE), 800);
        copied = true;
      }
    } catch (error) {
      copied = false;
    }

    if (!copied) {
      copied = this.#legacyCopy(DISCOUNT_CODE);
    }

    if (!copied) return;

    copyButton.textContent = 'Copied!';
    copyButton.classList.add('is-copied');

    window.clearTimeout(this.#copyResetTimeout);
    this.#copyResetTimeout = window.setTimeout(() => {
      copyButton.textContent = originalLabel;
      copyButton.classList.remove('is-copied');
    }, 2200);
  };

  #copyResetTimeout;

  #hasAlreadyClaimed() {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === 'true';
    } catch (error) {
      return false;
    }
  }

  #setClaimed() {
    try {
      window.localStorage.setItem(STORAGE_KEY, 'true');
    } catch (error) {
      // Storage may be unavailable (private browsing, disabled cookies, etc.).
      // The customer can still see and copy the code for this session.
    }
  }

  /**
   * @returns {boolean} Whether a discount confirmation is pending (and clears the flag).
   */
  #consumePendingConfirmation() {
    try {
      const pending = window.sessionStorage.getItem(PENDING_KEY) === 'true';
      if (pending) window.sessionStorage.removeItem(PENDING_KEY);
      return pending;
    } catch (error) {
      return false;
    }
  }

  #setPendingConfirmation() {
    try {
      window.sessionStorage.setItem(PENDING_KEY, 'true');
    } catch (error) {
      // Storage may be unavailable. The discount still applies server-side via
      // the redirect regardless — the customer just won't see the automatic
      // confirmation pop open and will see it on next manual open instead.
    }
  }

  /**
   * Hides the floating gift-button trigger once the customer has claimed
   * their prize, so it can't be reopened to (attempt to) claim a second one.
   * It's a sibling of this component in the markup (not a descendant), so it
   * can't be a `ref` — found via the shared `.spin-to-win-widget` wrapper instead.
   * Setting `hidden` alone isn't enough to hide it: the launcher's own
   * `display: flex` class rule has equal specificity and wins the cascade, so
   * an inline `display: none` is set too, without touching that existing rule.
   */
  #hideLauncher() {
    const launcher = this.closest('.spin-to-win-widget')?.querySelector('.spin-to-win-launcher');
    if (!launcher) return;

    launcher.hidden = true;
    launcher.style.display = 'none';
  }

  #openDialog() {
    const dialogComponent = this.closest('dialog-component');
    if (dialogComponent && typeof dialogComponent.showDialog === 'function') {
      dialogComponent.showDialog();
    }
  }

  #showResultScreen({ animate }) {
    const { wheelScreen, resultScreen } = this.refs;

    wheelScreen.hidden = true;
    resultScreen.hidden = false;

    if (animate) this.#launchConfetti();
  }

  /**
   * @param {Promise<unknown>} promise
   * @param {number} ms
   * @returns {Promise<unknown>}
   */
  #withTimeout(promise, ms) {
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error('timed out')), ms);
      promise.then(
        (value) => {
          window.clearTimeout(timer);
          resolve(value);
        },
        (error) => {
          window.clearTimeout(timer);
          reject(error);
        }
      );
    });
  }

  /**
   * @param {string} text
   * @returns {boolean} Whether the copy succeeded.
   */
  #legacyCopy(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-1000px';
    textarea.style.left = '-1000px';
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, text.length);

    let succeeded = false;
    try {
      succeeded = document.execCommand('copy');
    } catch (error) {
      succeeded = false;
    }

    document.body.removeChild(textarea);
    return succeeded;
  }

  #launchConfetti() {
    const { confetti } = this.refs;
    const colors = ['#d4af37', '#f3efe7', '#c76a3b', '#8fa9b8'];
    const pieceCount = 42;

    const fragment = document.createDocumentFragment();

    for (let i = 0; i < pieceCount; i += 1) {
      const piece = document.createElement('span');
      piece.className = 'spin-to-win__confetti-piece';

      const angle = Math.random() * 360;
      const distance = 70 + Math.random() * 130;
      const x = Math.cos((angle * Math.PI) / 180) * distance;
      const y = Math.sin((angle * Math.PI) / 180) * distance - 40;
      const rotation = Math.random() * 520 - 260;
      const width = 5 + Math.random() * 5;
      const height = width * (0.4 + Math.random() * 0.5);
      const color = colors[Math.floor(Math.random() * colors.length)];
      const delay = Math.random() * 150;
      const duration = 900 + Math.random() * 500;

      piece.style.setProperty('--stw-x', `${x}px`);
      piece.style.setProperty('--stw-y', `${y}px`);
      piece.style.setProperty('--stw-r', `${rotation}deg`);
      piece.style.width = `${width}px`;
      piece.style.height = `${height}px`;
      piece.style.background = color;
      piece.style.animationDelay = `${delay}ms`;
      piece.style.animationDuration = `${duration}ms`;

      piece.addEventListener('animationend', () => piece.remove());
      fragment.appendChild(piece);
    }

    confetti.appendChild(fragment);
  }
}

if (!customElements.get('spin-to-win')) {
  customElements.define('spin-to-win', SpinToWin);
}
